/*
  IMRI, 26/09/2026: mobile thumbnail strip under the product gallery (products with 5+ media).
  Rendered by snippets/product-media-gallery.liquid. Works in RTL and LTR: every scroll is a
  delta measured with getBoundingClientRect, never an absolute scrollLeft (which is negative in
  RTL and breaks Dawn's own thumbnail code). Only the two horizontal lists move; the page never
  scrolls vertically.
*/
if (!customElements.get('imri-mobile-thumbs')) {
  customElements.define(
    'imri-mobile-thumbs',
    class ImriMobileThumbs extends HTMLElement {
      connectedCallback() {
        this.list = document.getElementById(this.dataset.list);
        this.strip = this.querySelector('.imri-mthumbs__list');
        if (!this.list || !this.strip) return;
        this.buttons = Array.from(this.querySelectorAll('.imri-mthumbs__btn'));
        this.ticking = false;
        this.lockUntil = 0;
        this.syncTimer = null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.buttons.forEach((btn) => btn.addEventListener('click', () => this.goTo(btn.dataset.target)));
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            if (Date.now() < this.lockUntil) return;
            this.syncFromList();
          });
        };
        this.list.addEventListener('scroll', this.onScroll, { passive: true });
        // A variant change (media-gallery.js setActiveMedia with prepend) moves the variant's
        // slide to the front of the main list. Put the strip in the same order, renumber it,
        // bring it back to its start edge and re-highlight.
        this.observer = new MutationObserver(() => {
          this.reorder();
          requestAnimationFrame(() => this.syncFromList());
        });
        this.observer.observe(this.list, { childList: true });
      }

      disconnectedCallback() {
        if (this.list && this.onScroll) this.list.removeEventListener('scroll', this.onScroll);
        if (this.observer) this.observer.disconnect();
        clearTimeout(this.syncTimer);
      }

      behavior() {
        return this.reduceMotion && this.reduceMotion.matches ? 'auto' : 'smooth';
      }

      reorder() {
        const byId = new Map(this.buttons.map((b) => [b.dataset.target, b]));
        const total = this.buttons.length;
        let n = 0;
        this.list.querySelectorAll(':scope > [data-media-id]').forEach((slide) => {
          const btn = byId.get(slide.dataset.mediaId);
          if (!btn) return;
          n += 1;
          this.strip.appendChild(btn.parentElement);
          btn.setAttribute('aria-label', `תמונה ${n} מתוך ${total}`);
        });
        this.buttons = Array.from(this.querySelectorAll('.imri-mthumbs__btn'));
        // 0 is the start edge in both RTL and LTR.
        this.strip.scrollTo({ left: 0 });
      }

      slideFor(target) {
        return this.list.querySelector(`:scope > [data-media-id="${target}"]`);
      }

      // Horizontal distance (px) that brings `el`'s centre to the centre of `box`.
      centerDelta(el, box) {
        const e = el.getBoundingClientRect();
        const b = box.getBoundingClientRect();
        return e.left + e.width / 2 - (b.left + b.width / 2);
      }

      goTo(target) {
        const slide = this.slideFor(target);
        if (!slide || !slide.getBoundingClientRect().width) return;
        // Ignore the list's scroll events while it glides, so the highlight doesn't flicker
        // through the photos in between. One timer, which also ends the lock.
        this.lockUntil = Date.now() + 700;
        this.list.scrollBy({ left: this.centerDelta(slide, this.list), behavior: this.behavior() });
        this.setActive(target);
        clearTimeout(this.syncTimer);
        this.syncTimer = window.setTimeout(() => {
          this.lockUntil = 0;
          this.syncFromList();
        }, 700);
      }

      syncFromList() {
        const slides = this.list.querySelectorAll(':scope > [data-media-id]');
        if (!slides.length) return;
        const box = this.list.getBoundingClientRect();
        const mid = box.left + box.width / 2;
        let best = null;
        let bestDist = Infinity;
        slides.forEach((s) => {
          const r = s.getBoundingClientRect();
          if (!r.width) return;
          const d = Math.abs(r.left + r.width / 2 - mid);
          if (d < bestDist) {
            bestDist = d;
            best = s;
          }
        });
        if (best) this.setActive(best.dataset.mediaId);
      }

      setActive(target) {
        let active = null;
        this.buttons.forEach((btn) => {
          if (btn.dataset.target === target) {
            btn.setAttribute('aria-current', 'true');
            active = btn;
          } else {
            btn.removeAttribute('aria-current');
          }
        });
        if (!active) return;
        const item = active.parentElement;
        const s = this.strip.getBoundingClientRect();
        const r = item.getBoundingClientRect();
        if (r.left < s.left || r.right > s.right) {
          this.strip.scrollBy({ left: this.centerDelta(item, this.strip), behavior: this.behavior() });
        }
      }
    }
  );
}
